import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { authentication } from '@microsoft/teams-js';
import {
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { setAuthTokenProvider } from '@/lib/api/client';
import { canUseMsal, env } from '@/lib/env';
import { useTeams } from '@/lib/teams/teams-context';

/**
 * Access token acquisition.
 *
 * Three strategies, resolved at runtime:
 *  1. `dev`   — VITE_AUTH_MODE=dev. No Entra; the API trusts DEV_AUTH_EMAIL.
 *  2. Teams   — `authentication.getAuthToken()` (silent SSO, no second login).
 *  3. Browser — MSAL redirect flow against Entra ID.
 *
 * The token NEVER touches localStorage/sessionStorage: MSAL is configured with
 * an in-memory cache, and the value is kept in a ref that dies with the tab.
 * (MSAL still needs a transient sessionStorage entry for the PKCE/state pair
 * during the redirect round trip — that entry contains no tokens.)
 */

export type AuthStatus =
  | 'initializing'
  | 'authenticating'
  | 'authenticated'
  /** Dev mode: the API accepts unauthenticated calls from a known identity. */
  | 'disabled'
  | 'unauthenticated'
  | 'error';

export interface AuthContextValue {
  status: AuthStatus;
  /** Current access token, or null in dev mode. */
  token: string | null;
  /** Returns a fresh token, refreshing silently when needed. */
  getToken: () => Promise<string | null>;
  /** Interactive sign-in (browser only; inside Teams SSO is automatic). */
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  account: { name: string | null; username: string | null } | null;
  error: Error | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Tokens are refreshed a minute before they expire. */
const EXPIRY_SKEW_MS = 60_000;
/** Fallback lifetime when we cannot read the token expiry. */
const ASSUMED_LIFETIME_MS = 5 * 60_000;

interface CachedToken {
  value: string;
  expiresAt: number;
}

/** Reads `exp` from a JWT without validating it (validation happens server side). */
function readJwtExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized)) as { exp?: number };
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

function buildMsalConfig(): Configuration {
  return {
    auth: {
      clientId: env.azureClientId ?? '',
      authority: env.azureAuthority ?? undefined,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Access/ID tokens live in memory only.
      cacheLocation: BrowserCacheLocation.MemoryStorage,
      // Transient PKCE verifier + state must survive the redirect; no tokens here.
      temporaryCacheLocation: BrowserCacheLocation.SessionStorage,
      // Required, not optional: MSAL refuses the redirect flow outright when the
      // cache is in memory and this is false — "in_mem_redirect_unavailable" —
      // because it cannot read the state back from the URL it lands on. It
      // looks at `cacheLocation`, not at `temporaryCacheLocation`, so setting
      // that one above is not enough. What travels in the cookie is the state
      // and the PKCE verifier for the seconds the redirect lasts; the tokens
      // stay in memory, which is the property that matters inside the Teams
      // iframe.
      storeAuthStateInCookie: true,
    },
    system: {
      allowNativeBroker: false,
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const teams = useTeams();
  const [status, setStatus] = useState<AuthStatus>('initializing');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [account, setAccount] = useState<AuthContextValue['account']>(null);

  const cacheRef = useRef<CachedToken | null>(null);
  const msalRef = useRef<PublicClientApplication | null>(null);
  const inFlightRef = useRef<Promise<string | null> | null>(null);
  const mountedRef = useRef(true);

  const scopes = useMemo(() => (env.azureApiScope ? [env.azureApiScope] : []), []);

  const storeToken = useCallback((value: string | null) => {
    if (!value) {
      cacheRef.current = null;
      setToken(null);
      return;
    }
    const expiry = readJwtExpiry(value);
    cacheRef.current = {
      value,
      expiresAt: expiry ?? Date.now() + ASSUMED_LIFETIME_MS,
    };
    setToken(value);
  }, []);

  /** Teams SSO: silent, no popup, no second password. */
  const acquireTeamsToken = useCallback(async (): Promise<string> => {
    const ssoToken = await authentication.getAuthToken();
    if (!ssoToken) throw new Error('Microsoft Teams no devolvió un token de sesión.');
    return ssoToken;
  }, []);

  const getMsalInstance = useCallback(async (): Promise<PublicClientApplication> => {
    if (msalRef.current) return msalRef.current;
    const instance = new PublicClientApplication(buildMsalConfig());
    await instance.initialize();
    await instance.handleRedirectPromise();
    msalRef.current = instance;
    return instance;
  }, []);

  const pickAccount = useCallback((instance: PublicClientApplication): AccountInfo | null => {
    const active = instance.getActiveAccount();
    if (active) return active;
    const [first] = instance.getAllAccounts();
    if (first) {
      instance.setActiveAccount(first);
      return first;
    }
    return null;
  }, []);

  /** Browser fallback: silent refresh, redirect only when interaction is required. */
  const acquireMsalToken = useCallback(
    async (allowRedirect: boolean): Promise<string | null> => {
      const instance = await getMsalInstance();
      const existing = pickAccount(instance);

      if (!existing) {
        if (!allowRedirect) return null;
        await instance.loginRedirect({ scopes, prompt: 'select_account' });
        return null; // The page navigates away.
      }

      setAccount({ name: existing.name ?? null, username: existing.username ?? null });

      try {
        const result = await instance.acquireTokenSilent({ scopes, account: existing });
        return result.accessToken;
      } catch (cause) {
        if (cause instanceof InteractionRequiredAuthError && allowRedirect) {
          await instance.acquireTokenRedirect({ scopes, account: existing });
          return null;
        }
        throw cause instanceof Error ? cause : new Error(String(cause));
      }
    },
    [getMsalInstance, pickAccount, scopes],
  );

  const acquireToken = useCallback(
    async (allowRedirect: boolean): Promise<string | null> => {
      if (env.isDevAuth) return null;
      if (teams.isInTeams) return acquireTeamsToken();
      if (!canUseMsal()) {
        throw new Error(
          'La autenticación con Microsoft Entra ID no está configurada (falta VITE_AZURE_CLIENT_ID o VITE_AZURE_TENANT_ID).',
        );
      }
      return acquireMsalToken(allowRedirect);
    },
    [acquireMsalToken, acquireTeamsToken, teams.isInTeams],
  );

  const getToken = useCallback(async (): Promise<string | null> => {
    if (env.isDevAuth) return null;

    const cached = cacheRef.current;
    if (cached && cached.expiresAt - EXPIRY_SKEW_MS > Date.now()) {
      return cached.value;
    }

    // Collapse concurrent refreshes into a single network round trip.
    if (inFlightRef.current) return inFlightRef.current;

    const request = (async () => {
      try {
        const fresh = await acquireToken(false);
        if (fresh && mountedRef.current) {
          storeToken(fresh);
          setStatus('authenticated');
          setError(null);
        }
        return fresh;
      } finally {
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = request;
    return request;
  }, [acquireToken, storeToken]);

  const signIn = useCallback(async (): Promise<void> => {
    if (env.isDevAuth) return;
    setStatus('authenticating');
    try {
      const fresh = await acquireToken(true);
      if (!mountedRef.current) return;
      if (fresh) {
        storeToken(fresh);
        setStatus('authenticated');
        setError(null);
      }
    } catch (cause) {
      if (!mountedRef.current) return;
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      setStatus('error');
    }
  }, [acquireToken, storeToken]);

  const signOut = useCallback(async (): Promise<void> => {
    cacheRef.current = null;
    setToken(null);
    setAccount(null);
    if (env.isDevAuth || teams.isInTeams) {
      setStatus(env.isDevAuth ? 'disabled' : 'unauthenticated');
      return;
    }
    const instance = msalRef.current;
    setStatus('unauthenticated');
    if (instance) {
      await instance.logoutRedirect();
    }
  }, [teams.isInTeams]);

  // Bootstrap once the Teams host has been resolved (or discarded).
  useEffect(() => {
    mountedRef.current = true;
    if (!teams.isReady) return;

    if (env.isDevAuth) {
      setStatus('disabled');
      return;
    }

    let cancelled = false;
    setStatus('authenticating');

    void (async () => {
      try {
        const fresh = await acquireToken(true);
        if (cancelled || !mountedRef.current) return;
        if (fresh) {
          storeToken(fresh);
          setStatus('authenticated');
        } else {
          // A redirect is in progress, or no account is available yet.
          setStatus('unauthenticated');
        }
      } catch (cause) {
        if (cancelled || !mountedRef.current) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [acquireToken, storeToken, teams.isReady]);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Hand the API client a callback instead of a value, so it always gets a
  // valid (possibly refreshed) token and we never persist it anywhere.
  useEffect(() => {
    setAuthTokenProvider(getToken);
    return () => setAuthTokenProvider(null);
  }, [getToken]);

  const value = useMemo<AuthContextValue>(
    () => ({ status, token, getToken, signIn, signOut, account, error }),
    [status, token, getToken, signIn, signOut, account, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de <AuthProvider>.');
  }
  return context;
}

/** True when the session query may run (token ready, or dev mode). */
export function isAuthSettled(status: AuthStatus): boolean {
  return status === 'authenticated' || status === 'disabled';
}
